import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { TrackingLinkContract } from '@/contracts/share'
import { buildTrackingLink } from '@/lib/data-client/share'
import { Copy, Link2, Plus } from 'lucide-react'
import { useState } from 'react'

export function TrackingLinkBuilder({
  formId,
  defaultSalesCode,
  defaultCampaignCode,
}: {
  formId: string
  defaultSalesCode: string
  defaultCampaignCode: string
}) {
  const [name, setName] = useState('Q2 業務開發')
  const [utmSource, setUtmSource] = useState('newsletter')
  const [utmMedium, setUtmMedium] = useState('email')
  const [utmCampaign, setUtmCampaign] = useState(defaultCampaignCode)
  const [salesCode, setSalesCode] = useState(defaultSalesCode)
  const [campaignCode, setCampaignCode] = useState(defaultCampaignCode)
  const [hiddenKey, setHiddenKey] = useState('region')
  const [hiddenValue, setHiddenValue] = useState('north')
  const [preview, setPreview] = useState<TrackingLinkContract | null>(null)
  const [copied, setCopied] = useState(false)

  async function buildPreview() {
    const link = await buildTrackingLink({
      formId,
      name,
      utmSource,
      utmMedium,
      utmCampaign,
      salesCode,
      campaignCode,
      hiddenFields: hiddenKey ? { [hiddenKey]: hiddenValue } : {},
    })
    setPreview(link)
  }

  async function copyUrl() {
    if (!preview) return
    await navigator.clipboard.writeText(preview.url)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1000)
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="連結名稱" value={name} onChange={setName} />
        <Field label="業務代碼" value={salesCode} onChange={setSalesCode} />
        <Field label="活動代碼" value={campaignCode} onChange={setCampaignCode} />
        <Field label="utm_source" value={utmSource} onChange={setUtmSource} />
        <Field label="utm_medium" value={utmMedium} onChange={setUtmMedium} />
        <Field label="utm_campaign" value={utmCampaign} onChange={setUtmCampaign} />
        <Field label="Hidden field key" value={hiddenKey} onChange={setHiddenKey} />
        <Field label="Hidden field value" value={hiddenValue} onChange={setHiddenValue} />
        <div className="md:col-span-2">
          <Button type="button" onClick={() => void buildPreview()}>
            <Plus className="size-4" />
            產生 tracking URL contract
          </Button>
        </div>
        <Card className="md:col-span-2 border-indigo-100 bg-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-indigo-950">URL 組成方式</div>
              <Badge variant="outline" className="bg-white">
                provider not configured
              </Badge>
            </div>
            <div className="mt-3 grid gap-2 text-sm text-indigo-900">
              <UrlPart
                label="UTM"
                value={`utm_source=${utmSource}&utm_medium=${utmMedium}&utm_campaign=${utmCampaign}`}
              />
              <UrlPart label="業務代碼" value={`sales_code=${salesCode}`} />
              <UrlPart label="活動代碼" value={`campaign_code=${campaignCode}`} />
              <UrlPart
                label="Hidden fields"
                value={hiddenKey ? `${hiddenKey}=${hiddenValue}` : '未設定'}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-slate-950">Contract preview</div>
            <Badge variant="outline">mock-safe</Badge>
          </div>
          {preview ? (
            <div className="mt-3 space-y-3">
              <div className="rounded-md border border-slate-200 bg-white p-3 text-sm">
                <div className="flex items-center gap-2 text-slate-500">
                  <Link2 className="size-4" />
                  URL
                </div>
                <div className="mt-2 break-all font-medium text-slate-950">{preview.url}</div>
              </div>
              <pre className="max-h-48 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-100">
                {JSON.stringify(preview, null, 2)}
              </pre>
              <Button variant="outline" onClick={() => void copyUrl()}>
                <Copy className="size-4" />
                {copied ? '已複製！' : '複製 URL'}
              </Button>
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">
              先輸入 UTM、hidden fields、業務代碼與活動代碼，再產生測試連結 preview。
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function UrlPart({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-indigo-100 bg-white px-3 py-2">
      <div className="text-xs text-indigo-600">{label}</div>
      <div className="mt-1 break-all font-medium">{value}</div>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input value={value} onChange={(event) => onChange(event.currentTarget.value)} />
    </div>
  )
}
